// Types
import { Order } from "@/types/supabase";

// Libs
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { translatePaidWith } from "@/lib/utils";
import { calculateOrderTotal, formatCurrency } from "@/lib/formatCurrency";
import { useEffect, useState } from "react";
import { useOrdersApi } from "@/hooks/useOrdersApi";

// Components
import { Button } from "@/components/atoms/button";
import OrderAssignDriver from "./order-assign-driver";
import StatusBadge from "@/components/orders/StatusBadge";
// Icons
import { Banknote, Bike, Map, Package } from "lucide-react";

export default function OrderDeliveryDetails({ order }: { order: Order }) {
  const { fetchOrderDriversHistory } = useOrdersApi();
  const [driversHistory, setDriversHistory] = useState<any[]>([]);

  useEffect(() => {
    const load = async () => {
      const list = await fetchOrderDriversHistory(Number(order.id));
      setDriversHistory(Array.isArray(list) ? list : []);
    };
    load();
  }, [order.id]);

  const formatReceptionTime = (receptionAt: string | null) => {
    if (!receptionAt) return "—";
    try {
      const date = parseISO(receptionAt);
      return format(date, "h:mma");
    } catch (error) {
      return "—";
    }
  };
  return (
    <article className="py-4 px-5 rounded-2xl space-y-4 border border-gray-200 flex-1">
      <header className="font-semibold text-black flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <span>Detalles del envío</span>
        <div className="flex flex-wrap items-center gap-2">
          {order.driver != null && (
            <Button
              variant="ghost"
              size="sm"
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs sm:text-sm"
              asChild
            >
              <Link to={`/drivers/${order.driver?.id}`}>
                <Map size={14} className="sm:size-4" />
                <span className="hidden xs:inline">Seguimiento</span>
                <span className="xs:hidden">Seguir</span>
              </Link>
            </Button>
          )}

          {order.status !== "cancelled" && order.status !== "completed" && (
            <>
              <OrderAssignDriver
                order={order}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs sm:text-sm"
                  >
                    <Bike size={14} className="sm:size-4" />
                    <span className="hidden sm:inline">
                      {order.driver?.id ? "Reasignar" : "Asignar conductor"}
                    </span>
                    <span className="sm:hidden">
                      {order.driver?.id ? "Reasignar" : "Asignar"}
                    </span>
                  </Button>
                }
              />
            </>
          )}
        </div>
      </header>
      <section className="text-sm">
        <div className="flex items-center gap-6 h-9">
          <div className="flex items-start gap-2 text-gray-500">
            <Bike size={18} />
            <span className="font-semibold text-gray-600">Driver:</span>
            {(() => {
              const list = driversHistory;
              if (!list || list.length === 0) {
                return <span className="text-gray-400">Sin asignar</span>;
              }
              const current = list[0];
              const previous = list[1];
              return (
                <div className="flex flex-col gap-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <span className="text-green-600 font-medium break-words">
                      {current?.driver?.name || current?.name || "Sin nombre"}
                    </span>
                    <span className="text-xs text-gray-400">(actual)</span>
                  </div>
                  {previous && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500 break-words">
                        Anterior:{" "}
                        {previous?.driver?.name ||
                          previous?.name ||
                          "Sin nombre"}
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
            <StatusBadge status={order.status} type="order" />
          </div>
        </div>
        <div className="flex items-center gap-2 text-gray-500 py-2">
          <Package size={16} className="sm:size-18 flex-shrink-0" />
          <span className="font-semibold text-gray-600 flex-shrink-0">
            Despachado:
          </span>
          <span className="break-words">
            {formatReceptionTime(order.dispatch_date)}
          </span>
        </div>
        <div className="flex flex-col xs:flex-row xs:items-center gap-2 text-gray-500 py-2">
          <div className="flex items-center gap-2">
            <Banknote size={16} className="sm:size-18 flex-shrink-0" />
            <span className="font-semibold text-gray-600 flex-shrink-0">
              Costo:
            </span>
          </div>
          <span className="break-words ml-6 xs:ml-0">
            {formatCurrency(calculateOrderTotal(order), order.currency)} (
            {translatePaidWith(order.paid_with)})
          </span>
        </div>
      </section>
    </article>
  );
}
